'use client';

import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Layers, MapPin, Scale, SearchCheck, Building2 } from 'lucide-react';
import styles from './WhyRotana.module.css';

const features = [
    {
        title: "End-to-End Ownership",
        desc: "We don’t just form companies — we support them for years with accounting, tax, banking, visas, and compliance.",
        icon: <Layers size={32} />,
        span: ""
    },
    {
        title: "Real Offices, Not Just Virtual",
        desc: "With 400+ coworking desks and 50+ private offices in Ras Al Khaimah, your company has a physical, compliant presence from day one.",
        icon: <Building2 size={32} />,
        span: styles.span2
    },
    {
        title: "Freezone + Mainland Expertise",
        desc: "We advise what is legally and financially correct, not what’s easiest to sell.",
        icon: <Scale size={32} />,
        span: ""
    },
    {
        title: "Transparent Pricing",
        desc: "No hidden visa quotas, renewal shocks, or banking surprises.",
        icon: <SearchCheck size={32} />,
        span: ""
    },
    {
        title: "RAK Advantage",
        desc: "Lower setup cost, faster approvals, and flexible office options compared to Dubai.",
        icon: <MapPin size={32} />,
        span: ""
    }
];

export default function WhyRotana() {
    return (
        <section className={styles.section}>
            <div className="container">
                <div className={styles.header}>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className={styles.title}
                    >
                        Why Businesses Choose <span className={styles.highlight}>RotanaBiz</span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className={styles.subtitle}
                    >
                        A premium business setup experience powered by technology and expertise.
                    </motion.p>
                </div>

                <div className={styles.grid}>
                    {features.map((feature, i) => (
                        <FeatureCard key={i} feature={feature} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function FeatureCard({ feature, index }: any) {
    const divRef = useRef<HTMLDivElement>(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!divRef.current) return;
        const rect = divRef.current.getBoundingClientRect();
        setMousePos({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    };

    return (
        <motion.div
            ref={divRef}
            onMouseMove={handleMouseMove}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className={`${styles.card} ${feature.span} glass-card-hover`}
        >
            <div
                className={styles.cardGlow}
                style={{ left: mousePos.x, top: mousePos.y }}
            />
            <div className={styles.cardPattern} />

            <div className={styles.iconBox}>
                {feature.icon}
            </div>
            <h3 className={styles.cardTitle}>{feature.title}</h3>
            <p className={styles.cardDesc}>{feature.desc}</p>
        </motion.div>
    );
}
